import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {RadioButton} from '../../../../../libs/shared/ui/src/components/booleanInputs/radioButton';
import {DropdownBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {CrmSalesforceListModel} from '../../../../models/api/crmSalesforceListModel';
import {CrmSalesforceMetadataModel} from '../../../../models/api/crmSalesforceMetadataModel';
import {fetchSalesforceAccountListAsyncAction} from '../../../../store/data/crmSync/crmSyncActions';
import {Skeleton} from '../../../common/skeleton';
import {CrmSyncDialogStep} from '../crmSyncDialogStep';
import {SalesforceListsDropdownContainer} from './dropdowns/salesforceListsDropdownContainer';
import {
  isMaxSalesforceAllAccountsReached,
  SalesforceAccountType,
  SalesforceAccountTypesEnum,
  SalesforceMaxAllAccounts,
} from './salesforceHelpers';

/*
 * Constants.
 */

const maxTooltipWidth = 240; // px.
const learnMoreCustomListUrl =
  'https://trailhead.salesforce.com/en/content/learn/modules/lex_customization/lex_customization_list';

enum ListErrorsEnum {
  LIST_DELETED = 'list_deleted',
  MAX_LIMIT_REACHED = 'max_limit-reached',
  UNKNOWN = 'unknown',
}

/*
 * Props.
 */

export interface SalesforceCrmSyncStepTwoProps {
  isConnected: boolean;
  isDisabled?: boolean;
  isAdmin: boolean;
  selectedAccountType?: SalesforceAccountType;
  isLoading?: boolean;
  salesforceMetadata?: CrmSalesforceMetadataModel;
  onSelectAccountType: HandlerOf<SalesforceAccountType>;
  onHasError: HandlerOf<boolean>;
}

/*
 * Style.
 */

const StyledRadioButton = styled(RadioButton)`
  margin-top: 12px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
  margin: 0 0 -3px 4px;
`;

const StyledTooltipLearnMoreSpan = styled.span`
  font-weight: ${fontWeights.bold};
  text-decoration: underline;
`;

const StyledDropdownBox = styled(DropdownBox)`
  display: inline-block;
  margin: 8px 0 0 24px;
`;

const StyledSkeleton = styled(Skeleton)`
  display: inline-block;
  margin: 8px 0 -2px 24px;
  width: 150px;
  height: 30px;
`;

const StyledErrorWrapperDiv = styled.div`
  margin-top: 4px;
`;

const StyledSalesforceListTotalAccountsSpan = styled.span`
  margin-left: 4px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledSalesforceMaxErrorsWrapperDiv = styled.div`
  margin: 8px 0 0 26px;
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.palette.red.shade40};
`;

/*
 * Component.
 */

export const SalesforceCrmSyncStepTwo: FC<SalesforceCrmSyncStepTwoProps> = (props) => {
  const {
    isConnected,
    isDisabled,
    selectedAccountType,
    isLoading,
    isAdmin,
    salesforceMetadata,
    onSelectAccountType,
    onHasError,
  } = props;

  const [salesforceList, setSalesforceList] = useState<CrmSalesforceListModel>();
  const [salesforceListError, setSalesforceListError] = useState<ListErrorsEnum>();
  const dispatch = useAppDispatch();
  const isFetchingLineInformation = useRef<boolean>(false);

  // If we do not have a list or error and we have a list selected, we are currently loading.
  const isLoadingList = Boolean(selectedAccountType?.listId && !salesforceList && !salesforceListError);

  const onSetError = useCallback(
    (error: ListErrorsEnum) => {
      onHasError(true);
      setSalesforceListError(error);
    },
    [onHasError],
  );

  const onFetchListNameAsync = useCallback(
    async (listId: string) => {
      isFetchingLineInformation.current = true;
      setSalesforceListError(undefined);
      try {
        const list = await dispatch(fetchSalesforceAccountListAsyncAction(listId));
        setSalesforceList(list);
        onSelectAccountType({
          type: SalesforceAccountTypesEnum.LIST,
          listId: list.id,
          totalAccounts: list.totalAccounts,
        });

        if ((list.totalAccounts ?? 0) > SalesforceMaxAllAccounts) {
          onSetError(ListErrorsEnum.MAX_LIMIT_REACHED);
        } else {
          onHasError(false);
        }
      } catch (error) {
        if (error.status === 404) {
          onSetError(ListErrorsEnum.LIST_DELETED);
          onSelectAccountType({
            type: SalesforceAccountTypesEnum.LIST,
            listId: undefined,
            totalAccounts: undefined,
          });
        } else {
          onSetError(ListErrorsEnum.UNKNOWN);
        }
      }
      isFetchingLineInformation.current = false;
    },
    [dispatch, onHasError, onSelectAccountType, onSetError],
  );

  // If we have a list already selected, we will not have a name when the dialog first loads.
  useEffect(() => {
    if (salesforceList || !selectedAccountType?.listId || isFetchingLineInformation.current) {
      return;
    }
    onFetchListNameAsync(selectedAccountType.listId);
  }, [selectedAccountType?.listId, salesforceList, onFetchListNameAsync]);

  const onSelectAccountList = (listId: string) => {
    onSelectAccountType({type: SalesforceAccountTypesEnum.LIST, listId});
    setSalesforceList(undefined);
    setSalesforceListError(undefined);
  };

  const onSelectAllAccounts = () => {
    setSalesforceList(undefined);
    setSalesforceListError(undefined);
    onHasError(false);
    onSelectAccountType({
      type: SalesforceAccountTypesEnum.ALL,
      totalAccounts: salesforceMetadata?.allAccountsCount,
    });
  };

  return (
    <CrmSyncDialogStep
      step={2}
      title={renderTitle()}
      isDisabled={!isConnected || isDisabled}
      isLoading={isLoading}
      renderLoadingState={renderLoadingState}
    >
      <RadioButton
        isDisabled={!isConnected || !isAdmin || isDisabled}
        isChecked={selectedAccountType?.type === SalesforceAccountTypesEnum.ALL}
        onChange={onSelectAllAccounts}
      >
        <Trans id="accounts-crm-salesforce-dialog-step-2-all-accounts">All accounts</Trans>
        {salesforceMetadata && (
          <StyledSalesforceListTotalAccountsSpan>
            ({salesforceMetadata.allAccountsCount.toLocaleString()})
          </StyledSalesforceListTotalAccountsSpan>
        )}
      </RadioButton>
      {maybeRenderMaxAccountsError(salesforceMetadata, selectedAccountType?.type)}
      {renderCustomListsOption(
        props,
        salesforceList,
        isLoadingList,
        salesforceListError,
        onSelectAccountList,
      )}
    </CrmSyncDialogStep>
  );
};

/*
 * Helpers.
 */

function maybeRenderMaxAccountsError(
  metadata?: CrmSalesforceMetadataModel,
  selectedAccountType?: SalesforceAccountTypesEnum,
) {
  if (
    !isMaxSalesforceAllAccountsReached(metadata) ||
    selectedAccountType !== SalesforceAccountTypesEnum.ALL
  ) {
    return null;
  }
  return (
    <StyledSalesforceMaxErrorsWrapperDiv>
      <Trans id="accounts-crm-salesforce-dialog-step-2-max-accounts">
        Account limit (100k) exceeded. Please select a Salesforce list view.
      </Trans>
    </StyledSalesforceMaxErrorsWrapperDiv>
  );
}

function renderLoadingState() {
  return <Skeleton width="100%" height="56px" />;
}

function renderTitle() {
  return <Trans id="accounts-crm-salesforce-dialog-step-2-title">Select accounts to sync</Trans>;
}

function renderCustomListTooltip() {
  return (
    <>
      <Trans id="accounts-crm-salesforce-dialog-step-2-tooltip">
        You can create and customize list views in Salesforce based on specific filters.
      </Trans>{' '}
      <StyledTooltipLearnMoreSpan onClick={() => openInBrowser(learnMoreCustomListUrl)}>
        <Trans id="accounts-crm-salesforce-dialog-step-2-tooltip-learn-more">Learn more</Trans>
      </StyledTooltipLearnMoreSpan>
    </>
  );
}

function renderCustomListsOption(
  props: SalesforceCrmSyncStepTwoProps,
  salesforceList: CrmSalesforceListModel | undefined,
  isLoadingListName: boolean,
  salesforceListError: ListErrorsEnum | undefined,
  onSelectAccountList: HandlerOf<string>,
) {
  const {isConnected, isAdmin, selectedAccountType, isDisabled, onSelectAccountType} = props;
  return (
    <>
      <StyledRadioButton
        isDisabled={!isConnected || !isAdmin || isDisabled}
        isChecked={selectedAccountType?.type === SalesforceAccountTypesEnum.LIST}
        onChange={() => {
          // Only update the value if we do not have the list selected.
          if (selectedAccountType?.type !== SalesforceAccountTypesEnum.LIST) {
            onSelectAccountType({type: SalesforceAccountTypesEnum.LIST});
          }
        }}
      >
        <Trans id="accounts-crm-salesforce-dialog-step-2-salesforce-list-view">Salesforce list view</Trans>
        <StyledTooltipCoordinator
          render={renderCustomListTooltip}
          placement="top"
          delay={0}
          persistent
          maxWidth={maxTooltipWidth}
        />
      </StyledRadioButton>
      {renderCustomListsDropdown(
        props,
        salesforceList,
        isLoadingListName,
        salesforceListError,
        onSelectAccountList,
      )}
    </>
  );
}

function renderCustomListsDropdown(
  props: SalesforceCrmSyncStepTwoProps,
  salesforceList: CrmSalesforceListModel | undefined,
  isLoadingListName: boolean,
  salesforceListError: ListErrorsEnum | undefined,
  onSelectAccountList: HandlerOf<string>,
) {
  const {isConnected, selectedAccountType, isAdmin, isDisabled} = props;
  return (
    <PopoverCoordinator
      isDisabled={!isConnected || !isAdmin || isLoadingListName || isDisabled}
      renderButton={(hasHover) =>
        renderCustomListDropdownButton(
          props,
          hasHover,
          isLoadingListName,
          salesforceList,
          salesforceListError,
        )
      }
      renderModal={(popoverProps) => (
        <SalesforceListsDropdownContainer
          popoverProps={popoverProps}
          selectedListId={selectedAccountType?.listId}
          onSelect={(list) => onSelectAccountList(list.id)}
        />
      )}
    />
  );
}

function renderCustomListDropdownButton(
  props: SalesforceCrmSyncStepTwoProps,
  hasHover: boolean,
  isLoadingListName: boolean,
  salesforceList?: CrmSalesforceListModel,
  salesforceListError?: ListErrorsEnum,
) {
  const {isAdmin, isDisabled} = props;
  if (isLoadingListName) {
    return <StyledSkeleton />;
  }
  return (
    <StyledDropdownBox
      hasHover={hasHover}
      isDisabled={!isAdmin || isDisabled}
      value={renderCustomListDropdownValueOrPlaceholder(salesforceList)}
      boxStyles={{margin: 0, borderRadius: '6px', display: 'inline-flex'}}
      hasError={Boolean(salesforceListError)}
      errorMessage={maybeRenderErrorMessage(salesforceListError)}
    />
  );
}

function renderCustomListDropdownValueOrPlaceholder(salesforceList?: CrmSalesforceListModel) {
  if (!salesforceList) {
    return <Trans id="accounts-crm-salesforce-dialog-step-2-dropdown-placeholder">Select list view</Trans>;
  }

  if (isUndefined(salesforceList.totalAccounts)) {
    return salesforceList.name;
  }
  return (
    <>
      {salesforceList.name}
      <StyledSalesforceListTotalAccountsSpan>
        {renderAccountTotal(salesforceList.totalAccounts)}
      </StyledSalesforceListTotalAccountsSpan>
    </>
  );
}

function renderAccountTotal(totalAccounts: number) {
  const count = totalAccounts <= SalesforceMaxAllAccounts ? totalAccounts : `${SalesforceMaxAllAccounts}+`;
  return <>({count.toLocaleString()})</>;
}

function maybeRenderErrorMessage(listNameError?: ListErrorsEnum) {
  if (!listNameError) {
    return undefined;
  }
  switch (listNameError) {
    case ListErrorsEnum.LIST_DELETED:
      return (
        <StyledErrorWrapperDiv>
          <Trans id="accounts-crm-salesforce-dialog-step-2-list-deleted-error">
            List has been deleted in Salesforce.
          </Trans>
        </StyledErrorWrapperDiv>
      );
    case ListErrorsEnum.MAX_LIMIT_REACHED:
      return (
        <StyledErrorWrapperDiv>
          <Trans id="accounts-crm-salesforce-dialog-step-2-list-max-limit-reached">
            Lists with more than {SalesforceMaxAllAccounts} accounts cannot be synced.
          </Trans>
        </StyledErrorWrapperDiv>
      );
    case ListErrorsEnum.UNKNOWN:
    default:
      return (
        <StyledErrorWrapperDiv>
          <Trans id="accounts-crm-salesforce-dialog-step-2-unknown-error">
            An error occurred loading the Salesforce list.
          </Trans>
        </StyledErrorWrapperDiv>
      );
  }
}
